import { Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import styled from 'styled-components';

import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import ReactSelect from 'dashboard/components/ReactSelect';
import { CaseState, StateTag } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import { Tag } from 'dashboard/models/tag';
import Checkbox from 'design-system/components/Checkbox';
import { Label, Paragraph } from 'design-system/components/Text';

const HeaderContainer = styled('div')`
  display: grid;
  grid-template-columns: 0.75fr 2fr 200px 100px;
  grid-column-gap: ${Spacing[2]};
  border-radius: ${Spacing[2]};
  flex-direction: row;
  align-items: center;
  width: 100%;

  padding: ${Spacing[3]} ${Spacing[4]};

  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const RowContainer = styled('div')`
  display: grid;
  grid-template-columns: 0.75fr 2fr 200px 100px;
  grid-column-gap: ${Spacing[2]};

  padding: ${Spacing[2]} ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.background.secondary};
`;

const CenteredContainer = styled('div')`
  display: flex;
  justify-content: center;
`;

const StyledParagraph = styled(Paragraph)`
  color: ${({ theme }) => theme.colorError};
  margin-top: ${Spacing[2]};
`;

interface StatusTagRowProps {
  state: CaseState;
  stateTag: StateTag;
  tagOptions: { label: string; value: string }[];
  'data-test': string;
  onChangeStateTag: (stateTag: StateTag) => void;
}

const StatusTagRow = ({
  state,
  stateTag,
  tagOptions,
  'data-test': dataTest,
  onChangeStateTag,
}: StatusTagRowProps) => {
  const {
    'available-tags': availableTags,
    'is-tag-required': isTagRequired,
    'is-multi-tags-allowed': isMultiTagsAllowed,
  } = stateTag;

  const handleClickIsTagRequired = () => {
    onChangeStateTag({
      ...stateTag,
      'is-tag-required': !isTagRequired,
    });
  };

  const handleClickIsMultiTagsAllowed = () => {
    onChangeStateTag({
      ...stateTag,
      'is-multi-tags-allowed': !isMultiTagsAllowed,
    });
  };

  const handleSelectTags = (tags: string[]) => {
    onChangeStateTag({
      ...stateTag,
      'available-tags': tags,
    });
  };

  const hasError = isTagRequired && availableTags.length === 0;

  return (
    <RowContainer data-test={dataTest}>
      <CaseStatusSticker caseState={state} />
      <div>
        <ReactSelect
          name='available-tags'
          isMulti={true}
          value={availableTags}
          options={tagOptions}
          portal={true}
          isClearable={true}
          onChange={handleSelectTags}
        />
        {hasError && <StyledParagraph size='sm'>Please include a list of tags.</StyledParagraph>}
      </div>
      <CenteredContainer>
        <Checkbox
          checked={isMultiTagsAllowed}
          value={isMultiTagsAllowed}
          onChange={handleClickIsMultiTagsAllowed}
          aria-label='Allow multi-select'
          data-test={`status-tag-row__is-multi-tags-allowed--${state.token}`}
        />
      </CenteredContainer>
      <CenteredContainer>
        <Checkbox
          checked={isTagRequired}
          value={isTagRequired}
          onChange={handleClickIsTagRequired}
          aria-label='Required'
          data-test={`status-tag-row__is-tag-required--${state.token}`}
        />
      </CenteredContainer>
    </RowContainer>
  );
};

export interface CaseStatusTableProps {
  currentEnvironment: Environment;
  states: CaseState[];
  stateTags: StateTag[];
  allTags: Tag[];
  onChange: (stateTags: StateTag[]) => void;
}

export const CaseStatusTable = ({
  currentEnvironment,
  states,
  stateTags,
  allTags,
  onChange,
}: CaseStatusTableProps) => {
  const onChangeStateTag = (stateTag: StateTag) => {
    const stateTagIndex = stateTags.findIndex(
      (st) => st['state-token'] === stateTag['state-token'],
    );
    const nextStateTags = [...stateTags];
    nextStateTags[stateTagIndex] = stateTag;

    onChange(nextStateTags);
  };

  const tagOptions = useMemo(() => {
    return allTags.map((tag) => {
      return {
        label: tag.attributes.name,
        value: tag.id,
      };
    });
  }, [allTags]);

  const statusTagRows = states.map((state: CaseState) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    let stateTag: StateTag = stateTags.find(
      (st) =>
        st['state-token'] === state.token && st['environment-type'] === currentEnvironment.type,
    );

    // TODO: (melissa) remove this when state tags are backfilled on case templates
    if (stateTag == null) {
      // if state tag is not defined in the configuration, use the default
      // and add it to the form values.
      stateTag = {
        'environment-type': currentEnvironment.type,
        'state-token': state.token,
        'available-tags': [],
        'is-tag-required': false,
        'is-multi-tags-allowed': true,
        'can-create-tags': false,
      };

      stateTags.push(stateTag);
      onChange(stateTags);
    }

    return (
      <StatusTagRow
        state={state}
        stateTag={stateTag}
        tagOptions={tagOptions}
        onChangeStateTag={onChangeStateTag}
        key={state.token}
        data-test={`status-tag__row--${state.token}`}
      />
    );
  });

  return (
    <>
      <HeaderContainer>
        <Label size='lg' weight='bold'>
          Status
        </Label>
        <Label size='lg' weight='bold'>
          Tags
        </Label>
        <CenteredContainer>
          <Label size='lg' weight='bold'>
            Allow multi-select
          </Label>
        </CenteredContainer>
        <CenteredContainer>
          <Label size='lg' weight='bold'>
            Required
          </Label>
        </CenteredContainer>
      </HeaderContainer>
      {statusTagRows}
    </>
  );
};

export default CaseStatusTable;
